import React from "react";
import type { ExportFormat } from "../../../Utils/Export/types";
import Tooltip from "../Tooltip/Tooltip";
import {
  EXPORT_FORMAT_DISPLAY,
  getExportFormatUnavailableReason,
} from "./exportFormatDisplay";
import styles from "./ExportFormatChoiceGrid.module.css";

export type ExportFormatChoiceGridProps = {
  formats: ExportFormat[];
  onChoose: (format: ExportFormat) => void;
  disabled?: boolean;
  /** When true, applies Track-only format restrictions (e.g. Word disabled). */
  trackExport?: boolean;
  /** List: icon left (pickers). Tiles: 3-up grid (export wizards). */
  variant?: "list" | "tiles";
};

const ExportFormatChoiceGrid: React.FC<ExportFormatChoiceGridProps> = ({
  formats,
  onChoose,
  disabled = false,
  trackExport = false,
  variant = "list",
}) => {
  const gridClass = variant === "tiles" ? styles.gridTiles : styles.gridList;

  return (
    <div className={gridClass}>
      {formats.map((fmt) => {
        const meta = EXPORT_FORMAT_DISPLAY[fmt];
        if (!meta) return null;

        const unavailableReason = getExportFormatUnavailableReason(fmt, trackExport);
        const isDisabled = disabled || !!unavailableReason;
        const cardClass = variant === "tiles" ? styles.cardTile : styles.cardList;
        const wrapClass =
          variant === "tiles" ? styles.iconWrapTile : styles.iconWrapList;

        const card = (
          <button
            type="button"
            className={cardClass}
            disabled={isDisabled}
            onClick={() => {
              if (isDisabled) return;
              onChoose(fmt);
            }}
            aria-label={`Export as ${meta.title}`}
            aria-disabled={isDisabled || undefined}
          >
            <span className={wrapClass}>
              <img
                src={meta.icon}
                alt=""
                className={styles.icon}
                draggable={false}
              />
            </span>
            <span className={styles.cardBody}>
              <span className={styles.cardTitle}>{meta.title}</span>
              <span className={styles.cardDescription}>{meta.description}</span>
            </span>
          </button>
        );

        if (unavailableReason) {
          return (
            <Tooltip
              key={fmt}
              text={unavailableReason}
              align="top"
              sideAlign="left"
            >
              <div className={styles.formatItemWrap}>{card}</div>
            </Tooltip>
          );
        }

        return (
          <div key={fmt} className={styles.formatItemWrap}>
            {card}
          </div>
        );
      })}
    </div>
  );
};

export default ExportFormatChoiceGrid;
